// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { Close } from '@carbon/icons-react';
import styles from './RunDetailPanel.module.css';

interface RunBase {
  document: string;
  site: string;
  rev: string;
  started: string;
}

interface CompletedRun extends RunBase {
  status: 'Completed';
  ready: number;
  sme: number;
  rejected: number;
}

interface FailedRun extends RunBase {
  status: 'Failed';
  message: string;
}

interface RunningRun extends RunBase {
  status: 'Running';
  progress: number;
  /** Only set when this run is a retry of a previously-Failed one — carried
   *  along so Cancel can restore the real failure reason instead of
   *  fabricating a new one (see BulkImportPage's handleCancel). */
  previousMessage?: string;
}

export type Run = CompletedRun | FailedRun | RunningRun;

interface RunDetailPanelProps {
  run: Run;
  onClose: () => void;
}

const STAGE_NAMES = ['OCR and page split', 'Structure detection', 'Step extraction'] as const;
/** Roughly how a real extraction run's time splits across those three
 * stages — fixed ratios rather than per-run data, since nothing upstream
 * reports actual stage timings yet (see BulkImportPage's own docstring on
 * `RUNS` being example content, not pipeline data). */
const STAGE_RATIOS = [0.18, 0.37, 0.45] as const;

function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/** Deterministic from the run's own item counts, so every completed run
 * shows a distinct-looking but stable breakdown without hand-authoring a
 * duration for all of `RUNS`. The last stage absorbs the rounding
 * remainder so the three durations always sum to the total exactly. */
function estimateStageTimings(run: CompletedRun) {
  const items = run.ready + run.sme + run.rejected;
  const totalSeconds = Math.round(60 + items * 1.4);
  const stages = STAGE_NAMES.map((name, i) => ({
    name,
    seconds: i === STAGE_NAMES.length - 1 ? 0 : Math.round(totalSeconds * STAGE_RATIOS[i]),
  }));
  const allocated = stages.reduce((sum, s) => sum + s.seconds, 0);
  stages[stages.length - 1].seconds = totalSeconds - allocated;
  return { totalSeconds, stages };
}

/**
 * Right-side detail drawer for a Bulk Import run, opened by clicking a row.
 * Same `.liveBar` (sticky eyebrow strip) + `.header` (title/subline, own
 * bottom border) composition as `ProcedurePreview`'s Live Preview pane —
 * this is that same panel language, not a one-off header design.
 */
export default function RunDetailPanel({ run, onClose }: RunDetailPanelProps) {
  const isCompleted = run.status === 'Completed';
  const timing = isCompleted ? estimateStageTimings(run) : null;

  return (
    <aside className={styles.panel} aria-labelledby="run-detail-title">
      <div className={styles.panelHeader}>
        <span className={styles.panelHeaderLabel}>Run detail</span>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close run detail"
          onClick={onClose}
        >
          <Close size={16} />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.header}>
          <h2 id="run-detail-title" className={styles.title}>
            Extraction run
          </h2>
          <div className={styles.subline}>
            <span>{run.document}</span>
            <span aria-hidden="true">·</span>
            <span>{run.site}</span>
            <span aria-hidden="true">·</span>
            <span>rev {run.rev}</span>
            <span aria-hidden="true">·</span>
            <span>{run.status}</span>
            {timing && (
              <>
                <span aria-hidden="true">·</span>
                <span>{formatDuration(timing.totalSeconds)} total</span>
              </>
            )}
          </div>
        </div>

        {isCompleted ? (
          <>
            <div className={styles.statRow}>
              <div className={styles.stat}>
                <span className={`${styles.statValue} ${styles.statOk}`}>{run.ready}</span>
                <span className={styles.statLabel}>Ready</span>
              </div>
              <div className={styles.stat}>
                <span className={`${styles.statValue} ${styles.statWarn}`}>{run.sme}</span>
                <span className={styles.statLabel}>SME review</span>
              </div>
              <div className={styles.stat}>
                <span className={`${styles.statValue} ${styles.statErr}`}>{run.rejected}</span>
                <span className={styles.statLabel}>Rejected</span>
              </div>
            </div>

            <div className={styles.section}>
              <span className={styles.eyebrow}>Stages</span>
              <ul className={styles.stageList}>
                {timing?.stages.map(stage => (
                  <li key={stage.name} className={styles.stageRow}>
                    <span>{stage.name}</span>
                    <span className={styles.stageDuration}>{formatDuration(stage.seconds)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className={styles.footerNote}>
              Extracted drafts land in the Library as drafts. Publishing one opens a request.
            </p>
          </>
        ) : run.status === 'Failed' ? (
          <div className={styles.section}>
            <span className={styles.eyebrow}>Failure reason</span>
            <p className={styles.failureMessage}>{run.message}</p>
          </div>
        ) : (
          <div className={styles.section}>
            <span className={styles.eyebrow}>Status</span>
            <p className={styles.failureMessage}>Extracting… {run.progress}%</p>
          </div>
        )}
      </div>
    </aside>
  );
}
